/**
 * Waitlist (§13, §14).
 *
 * Phase 1 is invite-only: "men and couples join a waitlist that releases in
 * proportion to signups within their own pincode cluster". Releasing is the one
 * genuinely destructive control in this console — it lets people into the
 * product and cannot be undone from here — so it takes two deliberate clicks and
 * states the count and the cluster in the confirmation.
 *
 * The ratio guardrail outranks the operator. While a cluster is over the
 * 70:30 line the API keeps men on the list no matter what is typed here, and
 * this screen says so before the click rather than after it. A manual release
 * that quietly undoes the automatic pause is how a guardrail stops being one.
 */
import { useCallback, useEffect, useState } from 'react';
import { CLUSTERS } from '@alap/shared';
import { ApiError, getWaitlistDepth, releaseWaitlist } from '../api';
import type { MetricsResponse, RatioSnapshotRow, ReleaseResponse } from '../api';
import { ErrorState, Skeletons, clusterName, formatClock } from './kit';

interface KnownDepth {
  waiting: number;
  at: number;
  source: 'endpoint' | 'release';
}

export default function Waitlist({
  metrics,
  loading,
  error,
  onRefresh,
}: {
  metrics: MetricsResponse | null;
  loading: boolean;
  error: ApiError | null;
  onRefresh: () => void;
}) {
  const [depths, setDepths] = useState<Record<string, KnownDepth>>({});
  const [depthEndpoint, setDepthEndpoint] = useState<'unknown' | 'present' | 'absent' | 'error'>('unknown');
  const [depthError, setDepthError] = useState<string | null>(null);

  const loadDepth = useCallback(async () => {
    try {
      const res = await getWaitlistDepth();
      if (res === null) {
        setDepthEndpoint('absent');
        return;
      }
      const now = Date.now();
      const next: Record<string, KnownDepth> = {};
      for (const row of res.clusters) {
        next[row.clusterId] = { waiting: row.waiting, at: now, source: 'endpoint' };
      }
      setDepths(next);
      setDepthEndpoint('present');
      setDepthError(null);
    } catch (err) {
      setDepthEndpoint('error');
      setDepthError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void loadDepth();
  }, [loadDepth]);

  function recordRelease(res: ReleaseResponse) {
    setDepths((prev) => ({
      ...prev,
      [res.clusterId]: { waiting: res.stillWaiting, at: Date.now(), source: 'release' },
    }));
  }

  if (loading && !metrics) {
    return (
      <>
        <Intro />
        <Skeletons count={4} height={104} />
      </>
    );
  }

  if (error && !metrics) {
    return (
      <>
        <Intro />
        <ErrorState
          title="Cluster state did not load"
          message={error.message}
          code={`${error.code}${error.status ? ` · HTTP ${error.status}` : ''}`}
          onRetry={onRefresh}
        />
      </>
    );
  }

  const ratios: RatioSnapshotRow[] = metrics?.ratios ?? [];
  const byCluster = new Map<string, RatioSnapshotRow>(ratios.map((r) => [r.clusterId, r]));

  // The launch sequence is the order (§14), then anything the API knows about
  // that is not on the launch list — a cluster nobody planned for is exactly the
  // one you want to notice.
  const ids = [
    ...CLUSTERS.map((c) => c.id),
    ...ratios.map((r) => r.clusterId).filter((id) => !CLUSTERS.some((c) => c.id === id)),
  ];

  const paused = ids.filter((id) => byCluster.get(id)?.paused).length;
  const knownDepths = ids.map((id) => depths[id]).filter((d): d is KnownDepth => d !== undefined);
  const totalWaiting = knownDepths.reduce((sum, d) => sum + d.waiting, 0);

  return (
    <>
      <Intro />

      <div className="summary">
        <div className={`summary-cell ${paused > 0 ? 'is-alert' : 'is-calm'}`}>
          <span className="figure num">{paused}</span>
          <span className="sub">{paused === 1 ? 'cluster with male intake paused' : 'clusters with male intake paused'}</span>
        </div>
        <div className="summary-cell">
          <span className="figure num">{ids.length - paused}</span>
          <span className="sub">clusters taking everyone</span>
        </div>
        <div className="summary-cell">
          <span className="figure num">
            {knownDepths.length === 0 ? '—' : totalWaiting.toLocaleString('en-IN')}
          </span>
          <span className="sub">
            {knownDepths.length === 0
              ? 'queue depth not reported yet'
              : `waiting across ${knownDepths.length} of ${ids.length} clusters`}
          </span>
        </div>
      </div>

      {depthEndpoint === 'absent' && (
        <div className="notice is-warn" style={{ marginBottom: 'var(--s-lg)' }}>
          <b>This API build does not report queue depth.</b> There is no{' '}
          <code>GET /admin/waitlist</code> route, so the numbers below are filled in from each
          release response as it comes back, and nothing is guessed in the meantime. Every other
          control on this screen works.
        </div>
      )}

      {depthEndpoint === 'error' && depthError && (
        <div className="notice is-bad" style={{ marginBottom: 'var(--s-lg)' }}>
          Queue depth could not be read: {depthError}
        </div>
      )}

      {error && metrics && (
        <div className="notice is-warn" style={{ marginBottom: 'var(--s-lg)' }}>
          Cluster state is from {formatClock(metrics.generatedAt)}; the last refresh failed —{' '}
          {error.message}
        </div>
      )}

      <div className="cluster-list">
        {ids.map((id) => (
          <ClusterRow
            key={id}
            clusterId={id}
            snapshot={byCluster.get(id)}
            depth={depths[id]}
            onReleased={recordRelease}
          />
        ))}
      </div>

      <p className="eyebrow" style={{ marginTop: 'var(--s-lg)' }}>
        Releases are recorded in the audit log against your handle, with the cluster, the count and
        every user id let through (§07)
      </p>
    </>
  );
}

function Intro() {
  return (
    <div className="page-intro">
      <h2 className="eyebrow">Waitlist</h2>
      <p>
        Queue depth per cluster, in launch order (§14). Releasing lets people into the product and
        cannot be undone from here — while a cluster's ratio is over the pause line the API keeps
        men on the list whatever is typed here, and says so in its answer (§13).
      </p>
    </div>
  );
}

function ClusterRow({
  clusterId,
  snapshot,
  depth,
  onReleased,
}: {
  clusterId: string;
  snapshot: RatioSnapshotRow | undefined;
  depth: KnownDepth | undefined;
  onReleased: (res: ReleaseResponse) => void;
}) {
  const meta = CLUSTERS.find((c) => c.id === clusterId);
  const paused = snapshot?.paused ?? false;
  const men = snapshot?.men ?? 0;
  const womenNb = snapshot?.womenNb ?? 0;
  const total = men + womenNb;
  const menPct = total === 0 ? 0 : Math.round((snapshot?.ratio ?? 0) * 100);

  const [count, setCount] = useState(10);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ReleaseResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function release() {
    setBusy(true);
    setFailure(null);
    try {
      const res = await releaseWaitlist({ clusterId, count });
      setResult(res);
      onReleased(res);
      setConfirming(false);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`cluster ${paused ? 'is-paused' : ''}`}>
      <div>
        <h3>{clusterName(clusterId)}</h3>
        <div className="where">
          {meta ? `${meta.city} · phase ${meta.phase}` : 'not on the launch list (§14)'}
        </div>
        <div className="chip-row" style={{ marginTop: 8 }}>
          {paused ? (
            <span className="chip chip-alta">male intake paused</span>
          ) : (
            <span className="chip chip-river">intake open</span>
          )}
          {total === 0 && <span className="chip">no members yet</span>}
        </div>
      </div>

      <div>
        <div className="depth">
          <span className="value">
            {depth ? depth.waiting.toLocaleString('en-IN') : '—'}
          </span>
          <span className="unit">waiting</span>
        </div>
        <div className="where">
          {depth
            ? depth.source === 'release'
              ? `measured at ${formatClock(new Date(depth.at).toISOString())}, after the last release`
              : `as of ${formatClock(new Date(depth.at).toISOString())}`
            : 'depth unknown until a release reports it'}
        </div>
        <div className="ratiobar-legend" style={{ marginTop: 8 }}>
          <span className="num">
            {men.toLocaleString('en-IN')} men / {womenNb.toLocaleString('en-IN')} women+NB
          </span>
          <span className="num">{total === 0 ? '—' : `${menPct}% men`}</span>
        </div>
      </div>

      <div className="release">
        {!confirming && (
          <div className="release-row">
            <label htmlFor={`n-${clusterId}`} className="sr-only">
              How many to release from {clusterName(clusterId)}
            </label>
            <input
              id={`n-${clusterId}`}
              className="input"
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(e) => {
                const n = Number(e.target.value);
                setCount(Number.isFinite(n) ? Math.max(1, Math.min(500, Math.trunc(n))) : 1);
              }}
              disabled={busy}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setResult(null);
                setFailure(null);
                setConfirming(true);
              }}
              disabled={busy}
            >
              Release {count}
            </button>
          </div>
        )}

        {confirming && (
          <div className="confirm" role="alertdialog" aria-label="Confirm release">
            <span>
              Let <b className="num">{count}</b> {count === 1 ? 'person' : 'people'} into{' '}
              {clusterName(clusterId)}? This cannot be undone from the console.
              {paused && ' Male intake is paused here, so only the women/NB queue will move.'}
            </span>
            <div className="row">
              <button type="button" className="btn btn-danger btn-sm" onClick={() => void release()} disabled={busy}>
                {busy ? 'Releasing…' : `Yes, release ${count}`}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className={`notice ${result.released > 0 ? 'is-good' : 'is-warn'}`} role="status">
            {result.message}
            {result.menSkipped > 0 && (
              <>
                {' '}
                <b className="num">{result.menSkipped}</b> held back by the ratio guardrail.
              </>
            )}
          </div>
        )}

        {failure && (
          <div className="notice is-bad" role="alert">
            {failure}
          </div>
        )}
      </div>
    </article>
  );
}
